"use client";

import { useState, useMemo, useEffect } from "react";
import {
  ChevronDown, ChevronUp, Table2, Maximize2, Search, Download,
  BarChart3, ArrowUpDown, ArrowUp, ArrowDown, LayoutGrid, List,
} from "lucide-react";
import { formatValue, statusColor, alignFor, semanticColor } from "./formatters";
import type { RichTable, RichColumnFormat } from "@/lib/caonabo/rich-content";
import ChatTableModal from "./ChatTableModal";

type SortDir = "asc" | "desc" | null;
type Props = { table: RichTable };

const TERRA = "#8B3A2E";
const TERRA_LIGHT = "#D77F70";

export default function ChatTableCard({ table }: Props) {
  const [expanded, setExpanded] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [showSummary, setShowSummary] = useState(true);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>(null);
  const [forceCardView, setForceCardView] = useState<boolean | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  // Responsive
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const cardView = forceCardView !== null ? forceCardView : isMobile;

  // Filter + sort
  const filteredRows = useMemo(() => {
    if (!search.trim()) return table.rows;
    const q = search.toLowerCase();
    return table.rows.filter((row) =>
      table.columns.some((col) => {
        const v = row[col.key];
        return v != null && String(v).toLowerCase().includes(q);
      })
    );
  }, [table.rows, table.columns, search]);

  const sortedRows = useMemo(() => {
    if (!sortKey || !sortDir) return filteredRows;
    return [...filteredRows].sort((a, b) => {
      const va = a[sortKey] ?? "";
      const vb = b[sortKey] ?? "";
      if (typeof va === "number" && typeof vb === "number") {
        return sortDir === "asc" ? va - vb : vb - va;
      }
      const sa = String(va).toLowerCase();
      const sb = String(vb).toLowerCase();
      return sortDir === "asc" ? sa.localeCompare(sb) : sb.localeCompare(sa);
    });
  }, [filteredRows, sortKey, sortDir]);

  function handleSort(key: string) {
    if (sortKey === key) {
      if (sortDir === "asc") setSortDir("desc");
      else if (sortDir === "desc") {
        setSortKey(null);
        setSortDir(null);
      }
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  function exportCSV() {
    const header = table.columns.map((c) => `"${c.label}"`).join(",");
    const body = sortedRows
      .map((row) =>
        table.columns
          .map((c) => `"${String(row[c.key] ?? "").replace(/"/g, '""')}"`)
          .join(",")
      )
      .join("\n");
    const blob = new Blob([`${header}\n${body}`], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${table.title.replace(/[^a-z0-9]/gi, "_").toLowerCase()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  const showSearch = table.rows.length > 5;
  const showViewToggle = table.rows.length > 0 && !isMobile;
  const hasSummary = !!(table.summary && table.summary.length > 0);

  return (
    <>
      <div
        className="caonabo-fade-in my-2 rounded-2xl border overflow-hidden"
        style={{
          background: "rgba(255,255,255,0.03)",
          borderColor: "rgba(139,58,46,0.25)",
        }}
      >
        {/* Header */}
        <div
          className="w-full flex items-center justify-between px-3 py-2 border-b"
          style={{
            background:
              "linear-gradient(to right, rgba(139,58,46,0.12), rgba(139,58,46,0.06))",
            borderBottomColor: "rgba(139,58,46,0.20)",
          }}
        >
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-2 flex-1 hover:opacity-80 transition-opacity min-w-0"
          >
            <div
              className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: "rgba(139,58,46,0.18)" }}
            >
              <Table2 className="h-3.5 w-3.5" style={{ color: TERRA_LIGHT }} />
            </div>
            <div className="flex flex-col items-start min-w-0">
              <span className="text-xs font-semibold text-text truncate w-full text-left">
                {table.title}
              </span>
              {table.subtitle ? (
                <span className="text-[10px] text-text-muted truncate w-full text-left">
                  {table.subtitle}
                </span>
              ) : (
                <span className="text-[10px] text-text-muted">
                  {filteredRows.length} {filteredRows.length === 1 ? "fila" : "filas"}
                  {search && ` (de ${table.rows.length})`}
                </span>
              )}
            </div>
          </button>
          <div className="flex items-center gap-0.5 flex-shrink-0">
            {hasSummary && (
              <IconBtn
                title={showSummary ? "Ocultar resumen" : "Mostrar resumen"}
                onClick={() => setShowSummary((s) => !s)}
                active={showSummary}
              >
                <BarChart3 className="h-3.5 w-3.5" />
              </IconBtn>
            )}
            {showViewToggle && (
              <IconBtn
                title={cardView ? "Vista tabla" : "Vista tarjetas"}
                onClick={() => setForceCardView(!cardView)}
                active={cardView}
              >
                {cardView ? <List className="h-3.5 w-3.5" /> : <LayoutGrid className="h-3.5 w-3.5" />}
              </IconBtn>
            )}
            <IconBtn title="Exportar CSV" onClick={exportCSV}>
              <Download className="h-3.5 w-3.5" />
            </IconBtn>
            <IconBtn title="Vista expandida" onClick={() => setModalOpen(true)}>
              <Maximize2 className="h-3.5 w-3.5" />
            </IconBtn>
            <IconBtn title={expanded ? "Colapsar" : "Expandir"} onClick={() => setExpanded(!expanded)}>
              {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </IconBtn>
          </div>
        </div>

        {expanded && (
          <div className="caonabo-fade-in">
            {hasSummary && showSummary && <SummaryStrip fields={table.summary!} />}
            {showSearch && (
              <div
                className="border-b px-2.5 py-2"
                style={{
                  borderBottomColor: "rgba(139,58,46,0.15)",
                  background: "rgba(0,0,0,0.10)",
                }}
              >
                <div
                  className="flex items-center gap-2 px-2 py-1 rounded-lg border focus-within:border-[#D77F70] transition-colors"
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    borderColor: "rgba(139,58,46,0.20)",
                  }}
                >
                  <Search className="h-3 w-3 text-text-muted flex-shrink-0" />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar en la tabla..."
                    className="flex-1 bg-transparent text-[11px] focus:outline-none placeholder-text-muted text-text"
                  />
                  {search && (
                    <span className="text-[10px] text-text-muted tabular-nums">
                      {sortedRows.length}/{table.rows.length}
                    </span>
                  )}
                </div>
              </div>
            )}
            {sortedRows.length === 0 ? (
              <EmptyState search={!!search} />
            ) : cardView ? (
              <CardList rows={sortedRows} columns={table.columns} />
            ) : (
              <TableView
                rows={sortedRows}
                columns={table.columns}
                sortKey={sortKey}
                sortDir={sortDir}
                onSort={handleSort}
              />
            )}
          </div>
        )}
      </div>

      <ChatTableModal data={table} open={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
}

// ── Sub-components ──────────────────────────────────────────────────

function IconBtn({
  children,
  onClick,
  title,
  active,
}: {
  children: React.ReactNode;
  onClick: () => void;
  title: string;
  active?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      className="p-1.5 rounded-lg transition-colors"
      style={
        active
          ? { color: TERRA_LIGHT, background: "rgba(139,58,46,0.18)" }
          : { color: "rgba(215,127,112,0.6)" }
      }
      onMouseEnter={(e) => {
        if (!active) {
          e.currentTarget.style.color = TERRA_LIGHT;
          e.currentTarget.style.background = "rgba(139,58,46,0.10)";
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          e.currentTarget.style.color = "rgba(215,127,112,0.6)";
          e.currentTarget.style.background = "transparent";
        }
      }}
    >
      {children}
    </button>
  );
}

function SummaryStrip({
  fields,
}: {
  fields: NonNullable<RichTable["summary"]>;
}) {
  return (
    <div
      className="border-b px-2.5 py-2 caonabo-fade-in"
      style={{
        borderBottomColor: "rgba(139,58,46,0.15)",
        background:
          "linear-gradient(135deg, rgba(139,58,46,0.10), rgba(139,58,46,0.04))",
      }}
    >
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1.5">
        {fields.slice(0, 8).map((f, i) => (
          <div
            key={i}
            className="flex flex-col px-2.5 py-1.5 rounded-lg border"
            style={{
              background: "rgba(255,255,255,0.05)",
              borderColor: "rgba(139,58,46,0.20)",
            }}
          >
            <span className="text-[9px] uppercase tracking-wide text-text-muted truncate font-medium">
              {f.label}
            </span>
            <span className="text-[12px] font-bold text-text truncate tabular-nums">
              {typeof f.value === "number" ? formatValue(f.value, f.format) : f.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function EmptyState({ search }: { search: boolean }) {
  return (
    <div className="px-4 py-8 text-center">
      <Table2 className="h-7 w-7 text-text-muted mx-auto mb-2 opacity-40" />
      <p className="text-[11px] text-text-muted">
        {search ? "Sin resultados para esta búsqueda" : "Sin datos"}
      </p>
    </div>
  );
}

function TableView({
  rows,
  columns,
  sortKey,
  sortDir,
  onSort,
}: {
  rows: Record<string, unknown>[];
  columns: RichTable["columns"];
  sortKey: string | null;
  sortDir: SortDir;
  onSort: (key: string) => void;
}) {
  return (
    <div className="overflow-x-auto" style={{ maxHeight: 320 }}>
      <table className="w-full text-[12px]">
        <thead
          className="sticky top-0 z-10"
          style={{ background: "rgba(20,20,28,0.92)" }}
        >
          <tr style={{ borderBottom: "1px solid rgba(139,58,46,0.20)" }}>
            {columns.map((col) => {
              const align = alignFor(col.format);
              const isActive = sortKey === col.key;
              return (
                <th
                  key={col.key}
                  onClick={() => onSort(col.key)}
                  className="px-3 py-1.5 font-semibold text-text-muted whitespace-nowrap cursor-pointer select-none transition-colors group"
                  style={{ textAlign: align }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = TERRA_LIGHT)}
                  onMouseLeave={(e) => (e.currentTarget.style.color = "")}
                >
                  <span
                    className="inline-flex items-center gap-1.5"
                    style={{ justifyContent: align === "right" ? "flex-end" : "flex-start" }}
                  >
                    {col.label}
                    {isActive ? (
                      sortDir === "asc" ? (
                        <ArrowUp className="h-2.5 w-2.5" style={{ color: TERRA_LIGHT }} />
                      ) : (
                        <ArrowDown className="h-2.5 w-2.5" style={{ color: TERRA_LIGHT }} />
                      )
                    ) : (
                      <ArrowUpDown className="h-2.5 w-2.5 opacity-0 group-hover:opacity-50 transition-opacity" />
                    )}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              className="hover:bg-white/5 transition-colors"
              style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}
            >
              {columns.map((col) => (
                <Cell key={col.key} col={col} value={row[col.key]} />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Cell({
  col,
  value,
}: {
  col: { key: string; label: string; format?: RichColumnFormat };
  value: unknown;
}) {
  const align = alignFor(col.format);
  if (col.format === "status" && value != null) {
    const s = statusColor(String(value));
    return (
      <td className="px-3 py-1.5">
        <span
          className="text-[10px] font-bold px-1.5 py-0.5 rounded"
          style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}` }}
        >
          {formatValue(value, "status")}
        </span>
      </td>
    );
  }
  const semStyle = semanticColor(col.key, value);
  return (
    <td
      className="px-3 py-1.5 whitespace-nowrap text-text"
      style={{
        textAlign: align,
        fontVariantNumeric: align === "right" ? "tabular-nums" : undefined,
        ...(semStyle
          ? Object.fromEntries(
              semStyle.split(";").filter(Boolean).map((p) => {
                const [k, v] = p.split(":").map((x) => x.trim());
                const camel = k.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
                return [camel, v];
              })
            )
          : {}),
      }}
    >
      {formatValue(value, col.format)}
    </td>
  );
}

function CardList({
  rows,
  columns,
}: {
  rows: Record<string, unknown>[];
  columns: RichTable["columns"];
}) {
  const titleCol = columns[0];
  const bodyCols = columns.slice(1);

  return (
    <div>
      {rows.map((row, i) => (
        <div
          key={i}
          className="px-3 py-2.5 hover:bg-white/5 transition-colors caonabo-fade-in"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}
        >
          {titleCol && (
            <div className="flex items-baseline justify-between gap-2 mb-1.5">
              <span className="text-[13px] font-semibold text-text truncate">
                {formatValue(row[titleCol.key], titleCol.format) || "—"}
              </span>
              {bodyCols[0] &&
                (bodyCols[0].format === "currency" ||
                  bodyCols[0].format === "money" ||
                  bodyCols[0].format === "number") && (
                  <span
                    className="text-[12px] font-bold tabular-nums"
                    style={{ color: TERRA_LIGHT }}
                  >
                    {formatValue(row[bodyCols[0].key], bodyCols[0].format)}
                  </span>
                )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
            {bodyCols
              .slice(
                bodyCols[0]?.format === "currency" ||
                  bodyCols[0]?.format === "money" ||
                  bodyCols[0]?.format === "number"
                  ? 1
                  : 0
              )
              .slice(0, 6)
              .map((col) => (
                <div
                  key={col.key}
                  className="flex items-center justify-between gap-1.5 min-w-0"
                >
                  <span className="text-[10px] text-text-muted truncate">
                    {col.label}:
                  </span>
                  <span className="text-[11px] tabular-nums truncate text-right text-text">
                    {formatValue(row[col.key], col.format)}
                  </span>
                </div>
              ))}
          </div>
        </div>
      ))}
    </div>
  );
}
